import React, {Component, PureComponent} from 'react';
import {bool, node, shape, string} from 'prop-types';
import {messageHandler} from '@sinclair-digital/stl-common/src/sinclair/sinclair.uiTools';
import {append, assoc, assocPath, nth, pathEq, pipe, prepend, reject, remove, update, propEq} from 'ramda';
import {elems, get, modify, set} from 'partial.lenses';
import {ValidatedField} from '@sinclair-digital/sbg-storyline-components/models';
import {default as Button} from '@sinclair-digital/sbg-storyline-components/src/components/Button';
import {default as Icon} from '@sinclair-digital/sbg-storyline-components/src/components/Icon';
import {default as TabControl} from '@sinclair-digital/sbg-storyline-components/src/components/TabControl';
import {Spinner} from '@sinclair-digital/sbg-storyline-components/src/components/Loading';
import {uploadFile} from '@sinclair-digital/sbg-storyline-components/utils';
import {appContextShape} from '@sinclair-digital/sbg-storyline-sdk/lib/storyline/AppContext';
import {hasGeneralSettingsErrors} from './GeneralSettings/General';
import {hasThemeSettingsErrors} from './ThemeSettings/Theme';
import {hasIntegrationSettingsErrors, Provider, CustomIntegrationPair} from './IntegrationSettings/Integration';
import {hasSocialSettingsErrors} from './SocialSettings/Social';
import {exclamationTriangle} from 'react-icons-kit/fa/exclamationTriangle';
import ThemeSettings from './ThemeSettings/ThemeSettings.jsx';
import PackageTypes from './PackageTypes/PackageTypes.jsx';
import SocialSettings from './SocialSettings/SocialSettings.jsx';
import GeneralSettings from './GeneralSettings/GeneralSettings.jsx';
import SourceTab from './Sources/SourceTab.jsx';
import Integration from './IntegrationSettings/IntegrationSettings.jsx';
import {
    adjustProductionUrl,
    adjustStagingUrl,
    createEmptyPropertySettings,
    fetchProperty,
    saveProperty,
    hasErrors,
    validate,
    Property,
    TabName,
} from './Property';
import styles from './PropertySettings.scss';
import {updatePackageTypes, fetchPackageTypes} from './PackageTypes/PackageType';
import {Source} from '@sinclair-digital/stl-common/src/models/Property';
import {addSource, SourceGroup} from './Sources/Sources';

class TabLabel extends PureComponent<{showError?: boolean; children: React.ReactNode}> {
    static propTypes = {
        children: node.isRequired,
        showError: bool,
    };

    render() {
        const {showError, children} = this.props;
        return showError ? (
            <span className={styles.errorIconWrapper}>
                {children}&nbsp;
                <Icon icon={exclamationTriangle} className={styles.warningIcon} />
            </span>
        ) : (
            children
        );
    }
}

interface InputEvent {
    target: HTMLInputElement;
}

// This should really be defined by sbg-storyline-sdk
interface AppContext {
    hasUserPermissionSet: (perm: string, contentId: string) => boolean;
}

interface PropertySettingsProps {
    uuid: string;
    appContext: AppContext;
}

interface PropertySettingsState {
    property: Property;
    isLoading: boolean;
    hasPermissions: boolean;
}

class PropertySettings extends Component<PropertySettingsProps, PropertySettingsState> {
    static propTypes = {
        uuid: string,
        appContext: shape(appContextShape).isRequired,
    };

    static defaultProps = {
        uuid: undefined,
    };

    constructor(props: PropertySettingsProps) {
        super(props);
        const hasPermissions = this.canCreateAndSave();
        this.state = {
            property: createEmptyPropertySettings(),
            isLoading: hasPermissions,
            hasPermissions,
        };
        !hasPermissions &&
            messageHandler('error', 'Permission denied', 'You don\'t have sufficient permissions to edit that property');
    }

    componentDidMount() {
        if (this.props.uuid) {
            this.fetchPropertySettings(this.props.uuid);
        } else {
            fetchPackageTypes()
                .then(updatePackageTypes([]))
                .then(packageTypes =>
                    this.setState(
                        pipe(
                            assocPath(['property', 'packageTypes'], packageTypes),
                            assoc('isLoading', false),
                        ),
                    ),
                )
                .catch(() => messageHandler('error', 'Failed to load package types', 'Try reloading the page.'));
        }
    }

    addCustomIntegrationPairs = () => {
        const newCustomIntegrationPair: CustomIntegrationPair = {key: {value: ''}, val: ''};
        this.setState(
            modify(['property', 'integrationSettings', 'customIntegrationPairs'], prepend(newCustomIntegrationPair)),
        );
    };

    onDeleteCustomIntegrationPair = (idx: number) => () =>
        this.setState(modify(['property', 'integrationSettings', 'customIntegrationPairs'], remove(idx, 1)));

    canCreateAndSave = () => {
        const canCreate = this.props.appContext.hasUserPermissionSet('property.create', this.props.uuid);
        const canEdit = this.props.appContext.hasUserPermissionSet('property.edit', this.props.uuid);
        return canCreate && canEdit;
    };

    fetchPropertySettings = (uuid: string) => {
        Promise.all([fetchProperty(uuid), fetchPackageTypes()])
            .then(([property, packageTypes]) => {
                this.setState(
                    pipe(
                        assoc('property', property),
                        assocPath(['property', 'packageTypes'], updatePackageTypes(property.packageTypes)(packageTypes)),
                        assoc('isLoading', false),
                    )
                );
            })
            .catch(() => {
                messageHandler('error', 'Failed to load property', 'Try reloading the page.');
            });
    };

    uploadLogoImageUrl = file => {
        uploadFile(file)
            .then(({originalUrl}: {originalUrl: string}) =>
                this.setState(assocPath(['property', 'generalSettings', 'imageUrl'], originalUrl)),
            )
            .catch(() => {
                messageHandler('error', 'Failed to upload image');
            });
    };

    onCancel = () => {
        if (this.props.uuid) {
            window.location.assign(`#property/${this.props.uuid}`);
        } else {
            window.location.assign(`#property/`);
        }
    };

    removeEPGProvider = (name: string, id: string) => {
        const providers = this.state.property.integrationSettings.epgProviders;

        const idType = name === 'gracenote' ? 'stationId' : 'registrationKey';

        const newProviderList = reject(pathEq(['providerConfig', idType], id), providers);

        this.setState(set(['property', 'integrationSettings', 'epgProviders'], newProviderList));
    };

    addEPGProvider = (providerData: Provider) => {
        this.setState(modify(['property', 'integrationSettings', 'epgProviders'], append(providerData)));
    };

    onRemoveTag = (name: SourceGroup, propertySource: string) => {
        const newPropertySources = reject(propEq('name', propertySource), this.state.property.sources[name]);
        this.setState(set(['property', 'sources', name], newPropertySources));
    };

    addIncludedSourceTag = (name: string) =>
        this.setState(modify(['property', 'sources', 'includedSources'], addSource(name)));

    addExcludedSourceTag = (name: string) => {
        this.setState(modify(['property', 'sources', 'excludedSources'], addSource(name)));
    };

    updateSources = (propertyNamePath: string[], sourcesLens: SourceGroup, property: Property) =>
        modify<Property>(['sources', sourcesLens, elems], (source: Source) => {
            if (source.name === get(propertyNamePath)(this.state.property)) {
                return {name: get(propertyNamePath)(property), locked: source.locked};
            }
            return source;
        })(property);

    onValidatedCustomIntegrationPairChange = (idx: number) => ({target: {value}}: InputEvent) => {
        const {customIntegrationPairs} = this.state.property.integrationSettings;
        const kvPair: CustomIntegrationPair | undefined = nth(idx, customIntegrationPairs);
        if (kvPair) {
            const updatedItem = set<CustomIntegrationPair>(['key', ValidatedField.value], value)(kvPair);

            this.setState(
                modify(['property', 'integrationSettings', 'customIntegrationPairs'], update(idx, updatedItem)),
            );
        }
    };

    onCustomIntegrationPairChange = (idx: number) => ({target: {value}}: InputEvent) => {
        const {customIntegrationPairs} = this.state.property.integrationSettings;
        const kvPair: CustomIntegrationPair | undefined = nth(idx, customIntegrationPairs);
        if (kvPair) {
            const updatedItem = assoc('val', value, kvPair);
            this.setState(
                modify(['property', 'integrationSettings', 'customIntegrationPairs'], update(idx, updatedItem)),
            );
        }
    };

    _onInputChange = (isValidatedField: boolean) => (tabName: TabName) => ({target: {name, value}}: InputEvent) => {
        const propertyNameLens = isValidatedField ? [tabName, name, 'value'] : [tabName, name];
        const updatedStateWithValue = set<Property>(propertyNameLens, value)(this.state.property);

        let sourcesGroup: SourceGroup | undefined;
        if (name === 'network') {
            sourcesGroup = 'includedSources';
        } else if (name === 'name') {
            sourcesGroup = 'includedSources';
        } else if (name === 'designatedMarketArea') {
            sourcesGroup = 'excludedSources';
        }

        this.setState({
            property: sourcesGroup
                ? this.updateSources(propertyNameLens, sourcesGroup, updatedStateWithValue)
                : updatedStateWithValue,
        });
    };

    onInputChange = this._onInputChange(false);

    onValidatedInputChange = this._onInputChange(true);

    onUrlChange = ({target: {value}}: InputEvent) => {
        this.setState(assocPath(['property', 'generalSettings', 'url', 'value'], value));
        this.setState(assocPath(['property', 'generalSettings', 'stagingUrl'], adjustStagingUrl(value)));
    };

    onUrlBlur = ({target: {value}}: InputEvent) => {
        this.setState(assocPath(['property', 'generalSettings', 'url', 'value'], adjustProductionUrl(value)));
    };

    onCheckboxChange = (checked: boolean, event: InputEvent) => {
        const mappedPackageTypes = this.state.property.packageTypes.map(el => {
            return el.value === event.target.id ? Object.assign({}, el, {checked}) : el;
        });
        this.setState(assocPath(['property', 'packageTypes'], mappedPackageTypes));
    };

    save = () => {
        if (!hasErrors(this.state.property)) {
            saveProperty(this.state.property);
        }
    };

    validateAndSave = () => {
        this.setState(modify('property', validate), this.save);
    };

    onGeneralSettingsChange = this.onInputChange('generalSettings');

    onThemeSettingsChange = this.onInputChange('themeSettings');

    onIntegrationSettingsChange = this.onInputChange('integrationSettings');

    onSocialChange = this.onInputChange('socialSettings');

    onSourceChange = this.onInputChange('sources');

    onGeneralSettingsValidatedInputChange = this.onValidatedInputChange('generalSettings');

    onThemeSettingsValidatedInputChange = this.onValidatedInputChange('themeSettings');

    onIntegrationSettingsValidatedInputChange = this.onValidatedInputChange('integrationSettings');

    onSocialValidatedInputChange = this.onValidatedInputChange('socialSettings');

    renderTabs = () => {
        const {property} = this.state;

        const themeTabHasErrors = hasThemeSettingsErrors(property.themeSettings);
        const generalTabHasErrors = hasGeneralSettingsErrors(property.generalSettings);
        const integrationTabHasErrors = hasIntegrationSettingsErrors(property.integrationSettings);
        const socialTabHasErrors = hasSocialSettingsErrors(property.socialSettings);

        const tabs = [
            {
                label: <TabLabel showError={generalTabHasErrors}>General</TabLabel>,
                pane: (
                    <GeneralSettings
                        isInvalid={generalTabHasErrors}
                        onImageUpload={this.uploadLogoImageUrl}
                        onUrlChange={this.onUrlChange}
                        onProdUrlBlur={this.onUrlBlur}
                        onChange={this.onGeneralSettingsChange}
                        onValidatedInputChange={this.onGeneralSettingsValidatedInputChange}
                        generalSettings={property.generalSettings}
                    />
                ),
            },
            {
                label: <TabLabel showError={themeTabHasErrors}>Theme</TabLabel>,

                pane: (
                    <ThemeSettings
                        isInvalid={themeTabHasErrors}
                        onValidatedInputChange={this.onThemeSettingsValidatedInputChange}
                        onChange={this.onThemeSettingsChange}
                        themeSettings={property.themeSettings}
                    />
                ),
            },
            {
                label: <TabLabel showError={integrationTabHasErrors}>Integration</TabLabel>,
                pane: (
                    <Integration
                        addCustomIntegrationPair={this.addCustomIntegrationPairs}
                        isInvalid={integrationTabHasErrors}
                        addEPGProvider={this.addEPGProvider}
                        removeEPGProvider={this.removeEPGProvider}
                        onChange={this.onIntegrationSettingsChange}
                        onDeleteCustomIntegrationPair={this.onDeleteCustomIntegrationPair}
                        onValidatedInputChange={this.onIntegrationSettingsValidatedInputChange}
                        onCustomIntegrationPairChange={this.onCustomIntegrationPairChange}
                        onValidatedCustomIntegrationPairChange={this.onValidatedCustomIntegrationPairChange}
                        integrationSettings={property.integrationSettings}
                    />
                ),
            },
            {
                label: 'Package types',
                pane: <PackageTypes onChange={this.onCheckboxChange} packageTypes={property.packageTypes} />,
            },
            {
                label: 'Sources',
                pane: (
                    <SourceTab
                        propertySources={this.state.property.sources}
                        addIncludedSourceTag={this.addIncludedSourceTag}
                        addExcludedSourceTag={this.addExcludedSourceTag}
                        onRemoveTag={this.onRemoveTag}
                    />
                ),
            },
            {
                label: <TabLabel showError={socialTabHasErrors}>Social</TabLabel>,
                pane: (
                    <SocialSettings
                        isInvalid={socialTabHasErrors}
                        onValidatedInputChange={this.onSocialValidatedInputChange}
                        onChange={this.onSocialChange}
                        socialSettings={property.socialSettings}
                        appContext={this.props.appContext}
                    />
                ),
            },
        ];
        return tabs;
    };

    renderControls = () => {
        return (
            <div className={styles.navButtonGroup}>
                <Button className={styles.navButton} onClick={this.onCancel} displayType="secondary">
                    cancel
                </Button>
                <Button className={styles.navButton} onClick={this.validateAndSave} displayType="primary">
                    save & close
                </Button>
            </div>
        );
    };

    render() {
        return (
            <div className={styles.PropertySettings}>
                <div className={styles.PropertySettingsHeader}>
                    {this.state.hasPermissions ? (
                        this.state.isLoading ? (
                            <div className={styles.spinnerLoading}>
                                <Spinner />
                            </div>
                        ) : (
                            <TabControl theme="dark" tabs={this.renderTabs()} controls={this.renderControls()} />
                        )
                    ) : null}
                </div>
            </div>
        );
    }
}
export default PropertySettings;
